import { useState, useEffect } from "react";
import { it } from "date-fns/locale";
import { useNavigation } from "react-day-picker";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Calendar as CalendarIcon, X } from "lucide-react";

// Datepicker globale uniforme: input testo GG/MM/AAAA (slash automatici) + icona calendario.
// Calendario sempre in italiano: caption con dropdown mese (nomi italiani) + dropdown anno scorrevole,
// intestazioni lun..dom, giorno selezionato come cerchio scuro, giorni fuori mese in grigio chiaro.
// value è memorizzato in ISO (AAAA-MM-GG); all'utente viene mostrato SEMPRE GG/MM/AAAA.
const pad = (n) => String(n).padStart(2, "0");
const MESI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const FROM_YEAR = 1920;
const TO_YEAR = new Date().getFullYear() + 10;

const isoToDisplay = (iso) => {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const g = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(iso);
  return g ? iso : "";
};

const toISO = (d) => (d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : "");

const displayToISO = (s) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (!m) return null;
  const dd = +m[1], mm = +m[2], yy = +m[3];
  if (mm < 1 || mm > 12 || dd < 1 || yy < 1900 || yy > 2100) return null;
  const dt = new Date(yy, mm - 1, dd);
  if (dt.getFullYear() !== yy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd) return null;
  return `${yy}-${pad(mm)}-${pad(dd)}`;
};

const mask = (raw) => {
  const d = raw.replace(/\D/g, "").slice(0, 8);
  let out = d.slice(0, 2);
  if (d.length >= 2) out += "/";
  if (d.length > 2) out += d.slice(2, 4);
  if (d.length >= 4) out += "/";
  if (d.length > 4) out += d.slice(4, 8);
  return out;
};

// Caption con dropdown mese/anno (react-day-picker).
function MonthYearCaption({ displayMonth }) {
  const { goToMonth } = useNavigation();
  const y = displayMonth.getFullYear();
  const m = displayMonth.getMonth();
  const years = [];
  for (let yy = TO_YEAR; yy >= FROM_YEAR; yy--) years.push(yy);
  const selCls = "pgp-cal-select rounded-md px-2 py-1 text-sm cursor-pointer focus:outline-none focus:ring-1 focus:ring-teal-500/50";
  return (
    <div className="flex justify-center gap-2 px-1 pt-1" data-testid="cal-caption">
      <select value={m} onChange={(e) => goToMonth(new Date(y, +e.target.value, 1))} aria-label="Mese" data-testid="cal-month" className={selCls}>
        {MESI.map((name, i) => <option key={i} value={i}>{name}</option>)}
      </select>
      <select value={y} onChange={(e) => goToMonth(new Date(+e.target.value, m, 1))} aria-label="Anno" data-testid="cal-year" className={selCls}>
        {years.map((yy) => <option key={yy} value={yy}>{yy}</option>)}
      </select>
    </div>
  );
}

const CAL_CLASSNAMES = {
  cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
  head_cell: "pgp-cal-head-cell rounded-md w-8 font-normal text-[0.8rem]",
  day_selected: "pgp-day-selected",
  day_today: "pgp-day-today",
  day_outside: "pgp-day-outside",
};

export default function DateInput({ value, onChange, testId, className = "", placeholder = "GG/MM/AAAA", disabled }) {
  const [text, setText] = useState(isoToDisplay(value));
  const [error, setError] = useState("");
  useEffect(() => { setText(isoToDisplay(value)); setError(""); }, [value]);

  const handleType = (raw) => {
    const masked = mask(raw);
    setText(masked);
    if (masked === "") { setError(""); onChange(""); return; }
    const iso = displayToISO(masked);
    if (iso) { setError(""); onChange(iso); }
    else if (masked.length === 10) { setError("Data non valida"); }
    else { setError(""); }
  };

  const selDate = value ? new Date(value) : undefined;

  return (
    <div>
      <div className="relative">
        <Input value={text} onChange={(e) => handleType(e.target.value)} data-testid={testId} disabled={disabled}
          inputMode="numeric" placeholder={placeholder} autoComplete="off" className={`pr-16 font-mono ${className}`} />
        {text && !disabled && (
          <button type="button" tabIndex={-1} aria-label="Cancella data" title="Cancella data" data-testid={testId ? `${testId}-clear` : undefined}
            onClick={() => { setText(""); setError(""); onChange(""); }}
            className="absolute right-9 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-red-500 hover:bg-slate-500/10">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" disabled={disabled} tabIndex={-1} aria-label="Apri calendario"
              className="absolute right-0 top-0 h-full px-2 text-slate-400 hover:text-amber-400 hover:bg-transparent">
              <CalendarIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-[#121824] border-slate-700 text-slate-100 pgp-datepicker" align="end">
            <Calendar mode="single" locale={it} weekStartsOn={1} showOutsideDays
              selected={selDate} defaultMonth={selDate}
              classNames={CAL_CLASSNAMES} components={{ Caption: MonthYearCaption }}
              onSelect={(d) => { const iso = toISO(d); setText(isoToDisplay(iso)); setError(""); onChange(iso); }} />
          </PopoverContent>
        </Popover>
      </div>
      {error && <p className="mt-1 text-[11px] text-red-400" data-testid={testId ? `${testId}-error` : undefined}>{error}</p>}
    </div>
  );
}
